import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crop, Image as ImageIcon } from "react-feather";
import { BrandContext } from "@/contexts/BrandContext/context";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import type { BrandDetails } from "@/types/brand";
import { BannerFramingModal } from "./BannerFramingModal";

/**
 * Customizations › Banner framing. Shows the two strips the templated join
 * page uses and opens the framing modal. Self-contained: it saves through
 * PUT /brand/banner, not the tab's Save button, because it uploads files.
 */
export function BannerFramingCard() {
  const { brandDetails, refetchBrandDetails } = useContext(BrandContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const brand = brandDetails as BrandDetails | null | undefined;
  const original = brand?.brandBannerImageOriginal || brand?.brandBannerImage || "";
  const desktop = brand?.brandBannerImage || original;
  const mobile = brand?.brandBannerImageMobile || desktop;
  const hasCustomFraming = Boolean(brand?.brandBannerCrop);

  return (
    <Card id="AS-banner-framing">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Crop className="w-5 h-5" />
          Banner framing
        </CardTitle>
        <CardDescription className="text-xs">
          How your company banner is cropped for the join page on desktop and
          mobile. Change the image itself under Company details.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!original ? (
          <EmptyState
            icon={<ImageIcon className="w-8 h-8" />}
            title="No company banner yet"
            body="Upload one under Company details to frame it here"
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/brand/settings?tab=company-details")}
              >
                Go to Company details
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-[5fr,2fr] gap-3 items-end">
              <figure className="space-y-1.5">
                <div className="aspect-[5/1] rounded-lg overflow-hidden border border-border bg-muted">
                  <img
                    src={desktop}
                    alt="Desktop banner strip"
                    className="w-full h-full object-cover"
                  />
                </div>
                <figcaption className="text-xs text-muted-foreground">Desktop · 5:1</figcaption>
              </figure>
              <figure className="space-y-1.5">
                <div className="aspect-[2/1] rounded-lg overflow-hidden border border-border bg-muted">
                  <img
                    src={mobile}
                    alt="Mobile banner strip"
                    className="w-full h-full object-cover"
                  />
                </div>
                <figcaption className="text-xs text-muted-foreground">Mobile · 2:1</figcaption>
              </figure>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {hasCustomFraming
                  ? "Using your custom framing."
                  : "Using the automatic centre crop."}
              </p>
              <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
                Adjust framing
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {open && original && (
        <BannerFramingModal
          imageSrc={original}
          initialCrop={brand?.brandBannerCrop}
          onClose={() => setOpen(false)}
          onSaved={refetchBrandDetails}
        />
      )}
    </Card>
  );
}
